import { styled } from '@mui/system'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import WbCloudyRoundedIcon from '@mui/icons-material/WbCloudyRounded'
import ThunderstormRoundedIcon from '@mui/icons-material/ThunderstormRounded'
import { colorPalette } from '../../../../../../../../style/colorPalette'

const DELAY_THRESHOLD_CAUTION = 0.1
const DELAY_THRESHOLD_DANGER = 0.3

export type ProgressVariance = {
  diff: number
  status: 'ahead' | 'behind' | 'on-track'
}

type AlertLevel = 'ok' | 'caution' | 'danger'

export const calcProgressVariance = (
  progressRate: number,
  scheduledProgressRate: number | undefined
): ProgressVariance | undefined => {
  if (scheduledProgressRate === undefined) return undefined
  const diff = progressRate - scheduledProgressRate
  const status = diff > 0 ? 'ahead' : diff < 0 ? 'behind' : 'on-track'
  return { diff, status }
}

const getAlertLevel = (variance: ProgressVariance): AlertLevel => {
  if (variance.status === 'ahead' || variance.status === 'on-track') return 'ok'
  const delay = -variance.diff
  if (delay <= DELAY_THRESHOLD_CAUTION) return 'ok'
  if (delay <= DELAY_THRESHOLD_DANGER) return 'caution'
  return 'danger'
}

export const formatProgressRate = (rate: number): string =>
  `${(rate * 100).toFixed(1)}%`

export const formatVarianceDiff = (diff: number): string => {
  const rounded = Math.round(diff * 1000) / 1000
  const sign = rounded > 0 ? '+' : rounded === 0 ? '±' : ''
  return `${sign}${(rounded * 100).toFixed(1)}%`
}

const varianceColor = (status: ProgressVariance['status']): string =>
  status === 'behind' ? colorPalette.pink[5] : colorPalette.green[6]

const VarianceText = styled('span', {
  shouldForwardProp: prop => prop !== 'status' && prop !== 'isSummary',
})<{
  status: ProgressVariance['status']
  isSummary: boolean
}>(({ status, isSummary }) => ({
  fontSize: isSummary ? '12px' : '10px',
  fontWeight: 'bold',
  marginLeft: '3px',
  color: varianceColor(status),
  pointerEvents: 'none',
  flexShrink: 0,
  whiteSpace: 'nowrap',
}))

const weatherIconStyle = {
  fontSize: '14px',
  verticalAlign: 'middle',
  marginLeft: '2px',
  marginRight: '3px',
} as const

const WeatherSunny = styled(Brightness7Icon)({
  ...weatherIconStyle,
  color: colorPalette.yellow[3],
})

const WeatherCloudy = styled(WbCloudyRoundedIcon)({
  ...weatherIconStyle,
  color: colorPalette.monotone[3],
})

const WeatherRainy = styled(ThunderstormRoundedIcon)({
  ...weatherIconStyle,
  color: colorPalette.blue[8],
})

const WeatherIcon = ({ level }: { level: AlertLevel }) => {
  switch (level) {
    case 'ok':
      return <WeatherSunny />
    case 'caution':
      return <WeatherCloudy />
    case 'danger':
      return <WeatherRainy />
  }
}

type VarianceIndicatorProps = {
  progressRate: number
  scheduledProgressRate: number | undefined
}

export const ProgressAlertIcon = ({
  progressRate,
  scheduledProgressRate,
}: VarianceIndicatorProps) => {
  const variance = calcProgressVariance(progressRate, scheduledProgressRate)
  if (!variance) return null

  return <WeatherIcon level={getAlertLevel(variance)} />
}

export const ProgressVarianceDiff = ({
  progressRate,
  scheduledProgressRate,
  isSummary,
}: VarianceIndicatorProps & { isSummary: boolean }) => {
  const variance = calcProgressVariance(progressRate, scheduledProgressRate)
  if (!variance) return null

  return (
    <VarianceText status={variance.status} isSummary={isSummary}>
      {formatVarianceDiff(variance.diff)}
    </VarianceText>
  )
}
